/**
 * NotaVocaleCard — il modo veloce di annotare una persona: si tiene premuto e
 * si racconta. L'AI trascrive, ripulisce e propone i tag.
 *
 * Progettata perché l'inserimento avvenga SUBITO dopo la videochiamata: un
 * bottone grande, nessun campo obbligatorio, e in alternativa una casella di
 * testo per chi preferisce scrivere. Una nota registrata in trenta secondi vale
 * infinitamente più di una scheda perfetta mai compilata.
 *
 * Target touch ≥ 40px: la console si usa anche da tablet.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, IconButton, Paper, Stack,
  TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import { AutoAwesome, Edit, Mic, Stop } from '@mui/icons-material';
import { tokens } from '../../theme';
import { formatSecondi } from './labels';
import { useRegistrazioneAudio } from '@shared/audio/useRegistrazioneAudio';
import { analizzaNotaVocale } from '../../services/reteContattiService';
import type { AnalizzaNotaVocaleResponse } from '../../types/reteContatti';

/** Tetto della registrazione: oltre, meglio due note separate. */
const MAX_SECONDI = 300;

interface Props {
  /** Nome della persona: dà contesto al modello (non viene loggato). */
  nomePersona: string;
  /** Analisi pronta: il chiamante apre la revisione. */
  onAnalisi: (analisi: AnalizzaNotaVocaleResponse, daNotaVocale: boolean) => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const NotaVocaleCard: React.FC<Props> = ({ nomePersona, onAnalisi, showSnack }) => {
  const [analizzando, setAnalizzando] = useState(false);
  const [modoTesto, setModoTesto] = useState(false);
  const [testo, setTesto] = useState('');

  /** Motivo per cui l'ultima registrazione non è stata utilizzabile. */
  const [scartata, setScartata] = useState<{ motivo: string; sentito: string } | null>(null);

  const analizza = async (
    payload: { audioBase64: string; mimeType: string } | { testo: string },
  ) => {
    setAnalizzando(true);
    setScartata(null);
    try {
      const analisi = await analizzaNotaVocale({ ...payload, nomePersona });

      // Niente racconto utilizzabile: si mostra cosa ha sentito il sistema e non
      // si apre la revisione. Meglio una nota in meno che una nota inventata.
      if (analisi.contenuto_utile === false) {
        setScartata({
          motivo: analisi.motivo_non_utile || 'Non ho ricavato nulla da questa registrazione.',
          sentito: analisi.trascrizione_grezza || '',
        });
        return;
      }

      onAnalisi(analisi, 'audioBase64' in payload);
      if ('testo' in payload) setTesto('');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Analisi non riuscita', 'error');
    } finally {
      setAnalizzando(false);
    }
  };

  const rec = useRegistrazioneAudio({
    maxSecondi: MAX_SECONDI,
    // Il sink riceve il Blob; il base64 si chiede solo quando serve davvero
    // (qui sì: la CF lo vuole inline). `meta.mimeType` è il formato REALE del
    // recorder — su iPad è mp4, e mandarlo sbagliato degraderebbe la trascrizione.
    onAudioPronto: async (_blob, meta) => {
      await analizza({ audioBase64: await meta.toBase64(), mimeType: meta.mimeType });
    },
  });

  const occupato = rec.occupato || analizzando;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.bone }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: tokens.color.mist,
          }}
        >
          Aggiungi una nota
        </Typography>
        <Tooltip title={modoTesto ? 'Torna alla voce' : 'Scrivi invece di parlare'}>
          <IconButton
            size="small"
            onClick={() => setModoTesto((v) => !v)}
            disabled={occupato}
            sx={{ color: tokens.color.mist }}
          >
            {modoTesto ? <Mic fontSize="small" /> : <Edit fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {rec.nonSupportato && !modoTesto && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Questo browser non registra audio: usa la modalità scritta.
        </Alert>
      )}

      {rec.errore && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={rec.resetErrore}>
          {rec.errore}
        </Alert>
      )}

      {/* Registrazione scartata: si dice COSA è stato sentito, così si capisce
          se è un problema di microfono o solo una prova. */}
      {scartata && (
        <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setScartata(null)}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: scartata.sentito ? 0.5 : 0 }}>
            {scartata.motivo}
          </Typography>
          {scartata.sentito && (
            <Typography variant="caption" sx={{ color: tokens.color.ash, fontStyle: 'italic' }}>
              Ho sentito: «{scartata.sentito}»
            </Typography>
          )}
        </Alert>
      )}

      <Collapse in={!modoTesto} unmountOnExit>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!rec.registrando ? (
            <Button
              variant="contained"
              startIcon={
                analizzando
                  ? <CircularProgress size={18} color="inherit" />
                  : <Mic />
              }
              onClick={rec.avvia}
              disabled={occupato || rec.nonSupportato}
              // Target touch generoso: si usa anche col dito su tablet.
              sx={{ minHeight: 44, px: 2.5, textTransform: 'none', fontSize: '0.9rem' }}
            >
              {analizzando ? 'Sto ascoltando e riordinando…' : 'Registra e racconta'}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={<Stop />}
                onClick={rec.ferma}
                sx={{ minHeight: 44, px: 2.5, textTransform: 'none', fontSize: '0.9rem' }}
              >
                Ferma
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 9, height: 9, borderRadius: '50%',
                    bgcolor: tokens.color.danger,
                    animation: 'pulsaRec 1.2s ease-in-out infinite',
                    '@keyframes pulsaRec': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.25 },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: tokens.font.mono, fontSize: '0.9rem',
                    color: tokens.color.graphite, fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatSecondi(rec.secondi)}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                  / {formatSecondi(MAX_SECONDI)}
                </Typography>
              </Stack>
            </>
          )}

          {!rec.registrando && !analizzando && (
            <Typography variant="caption" sx={{ color: tokens.color.mist, flex: 1 }}>
              Racconta liberamente: chi è, cosa fa, cosa cerca, cosa potrebbe nascere.
            </Typography>
          )}
        </Stack>
      </Collapse>

      <Collapse in={modoTesto} unmountOnExit>
        <Stack spacing={1.5}>
          <TextField
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            size="small"
            placeholder="Es: conosciuto in videochiamata, ha un'azienda di installazioni a Cagliari, cerca un software per i preventivi, potrebbe passarmi lavori di progettazione."
            disabled={analizzando}
          />
          <Box>
            <Button
              variant="contained"
              startIcon={
                analizzando ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />
              }
              onClick={() => analizza({ testo: testo.trim() })}
              disabled={analizzando || testo.trim().length < 10}
              sx={{ minHeight: 40, textTransform: 'none' }}
            >
              {analizzando ? 'Analizzo…' : 'Analizza il racconto'}
            </Button>
          </Box>
        </Stack>
      </Collapse>

      {analizzando && (
        <Box
          sx={{
            mt: 1.5, p: 1, borderRadius: 1,
            bgcolor: alpha(tokens.color.success, 0.06),
          }}
        >
          <Typography variant="caption" sx={{ color: tokens.color.ash }}>
            Nulla viene salvato finché non confermi.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
